import { useQuery } from '@tanstack/react-query';
import { productsApi } from '@/api/products.api';
import { queryKeys } from '@/hooks/queryKeys';
import { ProductCard } from './ProductCard';
import { ProductCardSkeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';

interface RelatedProductsProps {
  categoryId: number;
  excludeProductId: number;
  className?: string;
  title?: string;
}

const LIMIT = 6;

export function RelatedProducts({
  categoryId,
  excludeProductId,
  className,
  title = 'You may also like',
}: RelatedProductsProps) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.products.relatedTo(excludeProductId),
    queryFn: () => productsApi.list({ categoryId, limit: LIMIT + 1 }),
    staleTime: 5 * 60_000,
  });

  const items = (data?.items ?? [])
    .filter((p) => p.id !== excludeProductId)
    .slice(0, LIMIT);

  return (
    <section
      aria-label="Related products"
      className={cn('flex flex-col gap-6', className)}
    >
      <h2 className="font-[family-name:var(--font-editorial)] text-2xl text-white">
        {title}
      </h2>
      <div className="-mx-4 px-4">
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 no-scrollbar">
          {isLoading
            ? Array.from({ length: LIMIT }).map((_, i) => (
                <div key={i} className="min-w-[240px] snap-start">
                  <ProductCardSkeleton />
                </div>
              ))
            : items.map((p) => (
                <div key={p.id} className="min-w-[240px] snap-start">
                  <ProductCard product={p} />
                </div>
              ))}
        </div>
      </div>
    </section>
  );
}
